import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVancouverSpeakeasies() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "underground-vancouver-bars-speakeasies";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles ? articles.find((art) => art.slug === ref_id) : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "laowai-section": "Laowai",
    "bagheera-section": "Bagheera",
    "narrow-section": "The Narrow Lounge",
    "key-party-section": "Key Party",
    "stock-room-section": "The Stock Room",
    "hello-goodbye-section": "Hello Goodbye",
    "arcana-section": "Arcana Spirit Lounge",
    "cantina-189-section": "Cantina 189",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // At least 3 references
  const referencesData = [
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Nightlife Guide",
    },
    {
      url: "https://www.translink.ca/rider-guide/nightbus-service",
      label: "TransLink NightBus Service",
    },
    {
      url: "https://www.hellobc.com",
      label: "HelloBC – Travel Safely in BC",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide a location array for each bar or relevant place from the conversation
  // The id should match the section, lat = Y, lng = X for <Marker coordY={lat} coordX={lng} />
  const locations = [
    {
      id: "laowai-section",
      lat: 49.27900,
      lng: -123.09680,
      popupText: "Laowai (Chinatown)",
    },
    {
      id: "bagheera-section",
      lat: 49.27950,
      lng: -123.10010,
      popupText: "Bagheera (Chinatown)",
    },
    {
      id: "narrow-section",
      lat: 49.26826,
      lng: -123.10064,
      popupText: "The Narrow Lounge (Mount Pleasant)",
    },
    {
      id: "key-party-section",
      lat: 49.26380,
      lng: -123.10060,
      popupText: "Key Party (Mount Pleasant)",
    },
    {
      id: "stock-room-section",
      lat: 49.27572,
      lng: -123.12255,
      popupText: "The Stock Room (Yaletown)",
    },
    {
      id: "hello-goodbye-section",
      lat: 49.27580,
      lng: -123.12200,
      popupText: "Hello Goodbye (Yaletown)",
    },
    {
      id: "arcana-section",
      lat: 49.28300,
      lng: -123.10600,
      popupText: "Arcana Spirit Lounge (Gastown)",
    },
    {
      id: "cantina-189-section",
      lat: 49.28199,
      lng: -123.11200,
      popupText: "Cantina 189 (Downtown)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Hidden Bars & Speakeasies: The Ultimate Guide</title>
        <meta
          name="description"
          content="A comprehensive guide to discovering Vancouver’s hidden bars and speakeasies, complete with locations, tips, and local regulations."
        />
        <meta
          name="keywords"
          content="Vancouver, hidden bars, speakeasies, nightlife, cocktails"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/underground-vancouver-bars-speakeasies.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Hidden Bars & Speakeasies: The Ultimate Guide"
        />
        <meta
          property="og:description"
          content="Explore Vancouver’s best-kept nightlife secrets, from unmarked doors to secret passwords."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/underground-vancouver-bars-speakeasies.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/underground-vancouver-bars-speakeasies.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Hidden Bars & Speakeasies: The Ultimate Guide"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver’s clandestine cocktail lounges and hidden speakeasies in this comprehensive guide."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/underground-vancouver-bars-speakeasies.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt={
                ref_data?.["image-desc"] ||
                "Vancouver’s hidden speakeasy scene background"
              }
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is well known for its scenic beauty and vibrant cultural
              scene, but hidden within the city’s lively neighborhoods is a
              thrilling underground nightlife. Speakeasies, secret bars, and
              concealed cocktail lounges transport guests back to the Prohibition
              era while offering innovative modern mixology. From unmarked doors
              to hidden passageways, Vancouver’s speakeasies deliver a sense of
              mystery and exclusivity—part of the fun is simply finding the
              place! This guide will introduce you to the best hidden bars in
              town, complete with addresses, coordinates, and insider tips to
              help you explore Vancouver’s clandestine cocktail culture.
            </p>

            {/* SECTION: Laowai */}
            <h2 id="laowai-section">Laowai</h2>
            <p
              ref={(el) => setSpotRef("laowai-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Laowai (Chinatown)</strong>{" "}
              <Marker
                coordY={49.27900}
                coordX={-123.09680}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Tucked away behind a humble dumpling shop labeled “BLND TGER
              Dumplings,” Laowai captures the spirit of 1920s Shanghai with its
              opulent decor and innovative, Asian-inspired cocktails. Guests
              enter by ordering a “Number 7” at the counter—a secret item not on
              the menu—prompting staff to usher them through a hidden freezer
              door. Expect creative baijiu-based cocktails and an intimate
              atmosphere reminiscent of old Shanghai glamour.
            </p>
            <ul>
              <li><strong>Address:</strong> 251 East Georgia St, Vancouver</li>
              <li><strong>Approx. Price:</strong> $$$ (cocktails ~$16)</li>
              <li><strong>Hours:</strong> Tue–Sun, 5pm–late</li>
            </ul>

            {/* SECTION: Bagheera */}
            <h2 id="bagheera-section">Bagheera</h2>
            <p
              ref={(el) => setSpotRef("bagheera-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bagheera (Chinatown)</strong>{" "}
              <Marker
                coordY={49.27950}
                coordX={-123.10010}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              A sister bar to Laowai, Bagheera is hidden behind a fake betting
              shop called the “Happy Valley Turf Club.” Featuring a vintage
              British-Indian colonial aesthetic, the bar is named after the
              panther from *The Jungle Book.* For entry, simply enter the
              betting office and ask to place your bet on “King Louie.” The
              playful ambience, impressive cocktail menu, and spice-infused
              drinks make this an unforgettable spot.
            </p>
            <ul>
              <li><strong>Address:</strong> 518 Main St, Vancouver</li>
              <li><strong>Approx. Price:</strong> $$$ (cocktails ~$15–$18)</li>
              <li><strong>Hours:</strong> Tue–Sun, 5pm–late</li>
            </ul>

            {/* SECTION: The Narrow Lounge */}
            <h2 id="narrow-section">The Narrow Lounge</h2>
            <p
              ref={(el) => setSpotRef("narrow-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Narrow Lounge (Mount Pleasant)</strong>{" "}
              <Marker
                coordY={49.26826}
                coordX={-123.10064}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              True to its name, The Narrow Lounge is a slim, graffiti-hidden bar
              just off Main Street. Look for a single red light near an
              unassuming door—if it’s on, the lounge is open! Inside, discover a
              cozy, candle-lit hideout with a laid-back vibe, classic cocktails,
              and local beers. It’s one of Vancouver’s first modern speakeasies,
              ideal for those seeking a more casual secret bar atmosphere.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1898 Main St, Vancouver
              </li>
              <li><strong>Approx. Price:</strong> $$ (cocktails ~$8–$14)</li>
              <li>
                <strong>Hours:</strong> Daily (Sun–Thu 5pm–1am, Fri–Sat
                5pm–2am)
              </li>
            </ul>

            {/* SECTION: Key Party */}
            <h2 id="key-party-section">Key Party</h2>
            <p
              ref={(el) => setSpotRef("key-party-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Key Party (Mount Pleasant)</strong>{" "}
              <Marker
                coordY={49.26380}
                coordX={-123.10060}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Hiding behind a faux accounting office façade labeled
              “Zottenburg &amp; Sons Accounting,” Key Party greets visitors with
              a playful, retro 1970s vibe. Once you push through the unmarked
              door, you enter a dimly lit lounge with funky decor, vintage
              furnishings, and tongue-in-cheek drink names. It’s a
              first-come-first-served spot, great for a whimsical night out.
            </p>
            <ul>
              <li><strong>Address:</strong> 2305 Main St, Vancouver</li>
              <li><strong>Approx. Price:</strong> $$ (cocktails ~$14–$18)</li>
              <li>
                <strong>Hours:</strong> Nightly (Sun–Thu 5pm–1am, Fri–Sat
                5pm–2am)
              </li>
            </ul>

            {/* SECTION: The Stock Room */}
            <h2 id="stock-room-section">The Stock Room</h2>
            <p
              ref={(el) => setSpotRef("stock-room-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Stock Room (Yaletown)</strong>{" "}
              <Marker
                coordY={49.27572}
                coordX={-123.12255}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Behind a casual burger joint called Hundy (1144 Homer Street),
              you'll find The Stock Room: a “tropical smuggler’s storeroom”
              meets upscale cocktail lounge. Enter through an unmarked door at
              the back of the restaurant and discover refined decor, creative
              tiki-esque drinks, and a bustling Yaletown crowd. 
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 1144 Homer St, Vancouver
              </li>
              <li><strong>Approx. Price:</strong> $$$ (cocktails ~$15)</li>
              <li>
                <strong>Hours:</strong> Tue–Sun, evenings until late (closed
                Mon)
              </li>
            </ul>

            {/* SECTION: Hello Goodbye */}
            <h2 id="hello-goodbye-section">Hello Goodbye</h2>
            <p
              ref={(el) => setSpotRef("hello-goodbye-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Hello Goodbye (Yaletown)</strong>{" "}
              <Marker
                coordY={49.27580}
                coordX={-123.12200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Part subterranean club, part speakeasy, Hello Goodbye is located
              beneath a barbershop at 1120 Hamilton Street. A discreet staircase
              leads you into this intimate, industrial-chic bar that often hosts
              DJs and late-night dancing. Expect a livelier scene—perfect for
              those seeking hidden bars with a nightclub edge.
            </p>
            <ul>
              <li><strong>Address:</strong> 1120 Hamilton St, Vancouver</li>
              <li><strong>Approx. Price:</strong> $$–$$$ (cocktails ~$14)</li>
              <li>
                <strong>Hours:</strong> Thu–Sat, 10pm–late (cover on busy
                nights)
              </li>
            </ul>

            {/* SECTION: Arcana Spirit Lounge */}
            <h2 id="arcana-section">Arcana Spirit Lounge</h2>
            <p
              ref={(el) => setSpotRef("arcana-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Arcana Spirit Lounge (Gastown)</strong>{" "}
              <Marker
                coordY={49.28300}
                coordX={-123.10600}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              Step into a world of vintage spiritualism at Arcana, concealed
              behind a storefront labeled “Mr. Wong’s Pet Psychic.” The bar
              features an occult-inspired theme and a menu of tarot-themed
              cocktails. Live events, like tarot readings or seance nights, add
              to the mystical experience. 
            </p>
            <ul>
              <li><strong>Address:</strong> 238 Abbott St, Vancouver</li>
              <li><strong>Approx. Price:</strong> $$$ (cocktails ~$16–$18)</li>
              <li>
                <strong>Hours:</strong> Tue–Sun, 5pm–12am (closed Mon)
              </li>
            </ul>

            {/* SECTION: Cantina 189 */}
            <h2 id="cantina-189-section">Cantina 189</h2>
            <p
              ref={(el) => setSpotRef("cantina-189-section", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Cantina 189 (Downtown)</strong>{" "}
              <Marker
                coordY={49.28199}
                coordX={-123.11200}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <p>
              For a Latin-inspired speakeasy vibe, Cantina 189 stands out.
              Hidden at 324 W Hastings, it appears unassuming from the outside,
              but inside you’ll find a chic “supper club” with lively music,
              handcrafted cocktails, and occasional themed nights. This new
              arrival on the speakeasy scene offers a membership program, but
              non-members are welcome to sample the unique drinks and ambiance.
            </p>
            <ul>
              <li>
                <strong>Address:</strong> 324 W Hastings St, Vancouver
              </li>
              <li><strong>Approx. Price:</strong> $$–$$$ (cocktails ~$14–$17)</li>
              <li>
                <strong>Hours:</strong> Typically Thu–Sun, check schedule
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Although these bars feel like stepping back into Prohibition,
              they’re fully licensed and follow British Columbia’s drinking
              laws. You must be 19 or older to enter, and staff will ask for ID
              at the door. Bars in Vancouver generally close around 1am–2am
              (some may extend to 3am on weekends), so arrive early to avoid
              missing out. Remember to respect the no-smoking policies and plan
              for safe transport—ride-sharing, taxis, or public NightBus routes
              are reliable ways to get home. Always drink responsibly, and be
              aware that bartenders can refuse service if you appear too
              intoxicated.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need a password or reservation?</h3>
              <p>
                Some spots, like Bagheera, use a fun password (“King Louie”),
                while others just require finding the hidden doorway (Laowai’s
                “Number 7” order). Many are walk-in only, but a few like Arcana
                or Cantina 189 accept reservations.
              </p>

              <h3>2. Are these speakeasies legal?</h3>
              <p>
                Yes. Despite the covert entrances, all venues listed are legally
                licensed bars operating under BC’s liquor regulations. The
                secrecy is just part of the immersive theme.
              </p>

              <h3>3. How should I dress?</h3>
              <p>
                Most hidden bars allow casual to smart-casual attire. Dressing
                up a bit can enhance the speakeasy vibe, but there’s no strict
                dress code unless stated.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s hidden bars and speakeasies offer a one-of-a-kind
              adventure, blending the excitement of a secret hideout with top
              cocktail craftsmanship. Whether you’re following cryptic clues in
              Chinatown or slipping beneath a barbershop in Yaletown, these
              clandestine venues prove that sometimes the best experiences are
              found behind unmarked doors. Remember to explore responsibly,
              respect local rules, and savor the thrill of Vancouver’s
              under-the-radar nightlife scene.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}